import { isRejectableStatus } from "@app/shared/models/enrichment-list.model";
import { EnrichmentPriceItem } from "@app/shared/models/enrichment-detail.model";

/**
 * Copy for the confirm shown before a PRICE save.
 *
 * Saving a price is not a neutral act since the per-price lifecycle cutover: the
 * backend stages whatever it receives, so the price lands on `designed`. For a price
 * that was `active`/`launched` that means it leaves the storefront until someone
 * publishes it again — a consequence the Save button says nothing about, and the one
 * this dialog exists to spell out.
 *
 * The wording changes with what is actually being saved. Warning that a price will
 * "leave the storefront" when nothing in the batch is on sale would be false, and a
 * dialog that cries wolf is one people learn to click through.
 */

/** What the user chose for the on-sale prices in a save. `keepLifecycleStatus`
 *  maps 1:1 onto the BFF's `?keepLifecycleStatus=true` on PATCH /enrichment/price. */
export interface PriceSaveDecision {
  keepLifecycleStatus: boolean;
}

/** A save nobody was asked about: the prices' lifecycle goes the usual way. */
export const DEFAULT_PRICE_SAVE_DECISION: PriceSaveDecision = {
  keepLifecycleStatus: false,
};

export interface SaveConfirmOption {
  label: string;
  hint: string;
}

/**
 * - `"plain"` — today's two-button confirm: nothing in the batch is on sale, or the
 *   user cannot keep a price on sale (no `enrichment:publish`), so there is no choice
 *   to offer and the save stages as before.
 * - `"choose"` — at least one price is on sale and the user may keep it there: the
 *   dialog asks Keep on sale / Take off sale before saving.
 */
export type SaveConfirmCopy =
  | {
      mode: "plain";
      title: string;
      message: string;
      primaryButtonText: string;
      liveCount: number;
    }
  | {
      mode: "choose";
      title: string;
      message: string;
      primaryButtonText: string;
      liveCount: number;
      options: { keep: SaveConfirmOption; offSale: SaveConfirmOption };
    };

/** Human list of price names — "Tier 1", "Tier 1 and Tier 2", "Tier 1, Tier 2 and 1 more". */
function nameList(names: string[]): string {
  const shown = names.slice(0, 2);
  const rest = names.length - shown.length;
  if (rest > 0) return `${shown.join(", ")} and ${rest} more`;
  if (shown.length === 2) return `${shown[0]} and ${shown[1]}`;
  return shown[0] ?? "";
}

function priceName(price: EnrichmentPriceItem, index: number): string {
  const raw = price.name;
  if (typeof raw === "string" && raw.trim()) return raw.trim();
  if (raw && typeof raw === "object") {
    const first = Object.values(raw as Record<string, unknown>).find(
      (v): v is string => typeof v === "string" && !!v.trim(),
    );
    if (first) return first.trim();
  }
  return price.id || `Price ${index + 1}`;
}

/**
 * Build the dialog copy for the prices a save is about to send.
 *
 * `live` here means on sale — the same `active`/`launched` pair Reject gates on, which
 * is why it reuses that predicate rather than restating the statuses.
 */
export function buildSaveConfirmCopy(
  prices: EnrichmentPriceItem[],
  { mayKeepLive = false }: { mayKeepLive?: boolean } = {},
): SaveConfirmCopy {
  const live = prices.filter((p) => isRejectableStatus(p.lifecycleStatus));
  const count = prices.length;

  if (!live.length) {
    return {
      mode: "plain",
      liveCount: 0,
      title: count === 1 ? "Save changes?" : `Save changes to ${count} prices?`,
      message:
        `After saving, ${
          count === 1 ? "this price is" : `these ${count} prices are`
        } in Designed. ` + "Nothing goes on sale until you publish.",
      primaryButtonText: "Save",
    };
  }

  const liveNames = nameList(live.map(priceName));
  const one = live.length === 1;
  const liveSubject = one ? `${liveNames} is` : `${liveNames} are`;

  // Only `enrichment:publish` holders may keep a price on sale through a save
  // (the BE 403s the flag for anyone else), so only they are offered the choice.
  if (mayKeepLive) {
    // A mixed batch gets one choice for the whole save; the prices that are not
    // on sale stage to Designed regardless, so say so rather than imply the
    // choice reaches them.
    const mixed = live.length < count;
    return {
      mode: "choose",
      liveCount: live.length,
      title: one ? "Keep this price on sale?" : "Keep these prices on sale?",
      message:
        `${liveSubject} on sale. Choose what happens to ${
          one ? "it" : "them"
        } when you save. Your edits are saved either way.` +
        (mixed ? " Prices not on sale go to Designed as usual." : ""),
      primaryButtonText: "Save",
      options: {
        keep: {
          label: "Keep on sale",
          // Not "right away": the customer apps pick the change up through
          // the channel sync, which is not instant.
          hint: `${one ? "Stays" : "Stay"} on sale. Customers get the new values once they sync to the app.`,
        },
        offSale: {
          label: "Take off sale",
          hint: one
            ? "Goes back to Designed and leaves the app until you publish it again."
            : "Go back to Designed and leave the app until you publish them again.",
        },
      },
    };
  }

  return {
    mode: "plain",
    liveCount: live.length,
    title: one
      ? "Saving takes this price off sale"
      : "Saving takes these prices off sale",
    message:
      `${liveSubject} on sale. Saving moves ${one ? "it" : "them"} back to ` +
      `Designed, and ${one ? "it leaves" : "they leave"} the app until someone ` +
      `with publish permission publishes ${one ? "it" : "them"} again. ` +
      "Your edits are saved.",
    primaryButtonText: "Save and take off sale",
  };
}

/**
 * Success toast for a price save. `liveCount` is how many of the saved prices were
 * on sale when the choice was offered — pass 0 when no choice was offered (nothing
 * live, or no permission), which keeps the plain "Saved".
 */
export function saveSuccessToast(
  liveCount: number,
  decision: PriceSaveDecision,
): string {
  if (liveCount <= 0) return "Saved";
  return decision.keepLifecycleStatus
    ? "Saved — still on sale"
    : "Saved — taken off sale, now Designed";
}

export type AssignLiveCopy =
  | {
      mode: "choose";
      message: string;
      options: { keep: SaveConfirmOption; offSale: SaveConfirmOption };
    }
  | { mode: "warn"; message: string };

export function buildAssignLiveCopy(
  liveCount: number,
  opts: { mayKeepLive: boolean },
): AssignLiveCopy | null {
  if (liveCount <= 0) return null;
  const one = liveCount === 1;

  if (opts.mayKeepLive) {
    return {
      mode: "choose",
      message: `Assigning changes ${liveCount} ${one ? "price that is" : "prices that are"} on sale.`,
      options: {
        keep: {
          label: one
            ? "Keep the live price on sale"
            : "Keep live prices on sale",
          hint: "Customers see the new categories once they sync to the app.",
        },
        offSale: {
          label: one
            ? "Take it off sale (back to Designed)"
            : "Take them off sale (back to Designed)",
          hint: one
            ? "It leaves the app until you publish it again."
            : "They leave the app until you publish them again.",
        },
      },
    };
  }

  return {
    mode: "warn",
    message:
      `Assigning takes ${liveCount} live ${one ? "price" : "prices"} back to ` +
      `Designed. ${one ? "It leaves" : "They leave"} the app until someone with ` +
      `publish permission publishes ${one ? "it" : "them"} again.`,
  };
}

export type AssignLiveChoice = "keep" | "offSale";

/**
 * The decision a bulk assign sends with every price PATCH.
 *
 * The author sees the keep-or-take-off choice only when the list rows say a live
 * price will change, and the rows can be stale. A hidden choice therefore keeps
 * live prices on sale, because a PATCH without the flag takes a live price off
 * sale. `picked` is the author's choice, or null when it was not on screen. The
 * BFF ignores the flag for a price that is not live, so sending it on every
 * PATCH is safe. Without `mayKeepLive` the flag is never sent, because the BFF
 * answers 403.
 */
export function resolveAssignDecision(opts: {
  mayKeepLive: boolean;
  picked: AssignLiveChoice | null;
}): PriceSaveDecision {
  return {
    keepLifecycleStatus: opts.mayKeepLive && opts.picked !== "offSale",
  };
}

export function buildAssignDesignedCopy(count: number): string | null {
  if (count <= 0) return null;
  return (
    `Assigning moves ${count} rejected or in-design ` +
    `${count === 1 ? "price" : "prices"} to Designed (ready to publish).`
  );
}
